import { useState } from 'react';

import { BookmarkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { SavedFilterForm } from './SavedFilterForm';

interface SavePopoverProps {
  hasActiveView: boolean;
  onSave: (name: string) => string | null;
  onUpdate?: () => void;
}

export const SavePopover = ({ hasActiveView, onSave, onUpdate }: SavePopoverProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showSaveAsNewForm, setShowSaveAsNewForm] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setShowSaveAsNewForm(false);
    }
  };

  const handleClose = () => {
    setShowSaveAsNewForm(false);
    setOpen(false);
  };

  // Show menu for existing views, form directly for new views
  const showMenu = hasActiveView && onUpdate;

  const renderContent = () => {
    if (!showMenu) {
      return <SavedFilterForm onSave={onSave} onSuccess={handleClose} onCancel={handleClose} />;
    }

    if (showSaveAsNewForm) {
      return (
        <SavedFilterForm
          onSave={onSave}
          onSuccess={handleClose}
          onCancel={() => setShowSaveAsNewForm(false)}
        />
      );
    }

    const menuItemClass =
      'relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm leading-5 outline-none select-none hover:bg-highlight-background';

    return (
      <div className="flex min-w-32 flex-col p-1">
        <button
          type="button"
          className={menuItemClass}
          onClick={() => {
            onUpdate();
            setOpen(false);
          }}
        >
          {t('common.save')}
        </button>
        <button type="button" className={menuItemClass} onClick={() => setShowSaveAsNewForm(true)}>
          {t('filters.views.save_as_new')}
        </button>
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <InfoTooltip content={t('filters.views.save')} side="top">
        <PopoverTrigger asChild>
          <Button size="icon-sm" variant="primary">
            <BookmarkIcon size={16} />
          </Button>
        </PopoverTrigger>
      </InfoTooltip>
      <PopoverContent align="end" className="w-auto p-0">
        {renderContent()}
      </PopoverContent>
    </Popover>
  );
};
